
// ─── PASTE INI KE src/App.js ───────────────────────────────────────────────
// Requires: npm install @supabase/supabase-js
// Buat file src/lib/supabase.js dulu dengan konten:
//   import { createClient } from '@supabase/supabase-js'
//   export const supabase = createClient('YOUR_URL', 'YOUR_KEY')

import { useState, useEffect } from 'react'
import { supabase } from './lib/supabase'

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = n => 'Rp ' + Number(n || 0).toLocaleString('id-ID')
const fmtDate = d => d ? new Date(d).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'

// ─── APP ROOT ─────────────────────────────────────────────────────────────────
export default function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('home')
  const [koperasi, setKoperasi] = useState(null)
  const [userProfile, setUserProfile] = useState(null)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      if (session) fetchProfile(session.user.id)
      setLoading(false)
    })
    const { data: listener } = supabase.auth.onAuthStateChange((_e, session) => {
      setSession(session)
      if (session) fetchProfile(session.user.id)
      else { setUserProfile(null); setKoperasi(null) }
    })
    return () => listener.subscription.unsubscribe()
  }, [])

  async function fetchProfile(uid) {
    const { data } = await supabase.from('users').select('*, koperasi(*)').eq('id', uid).single()
    if (data) { setUserProfile(data); setKoperasi(data.koperasi) }
  }

  if (loading) return <div style={s.center}><div style={s.spinner} /></div>
  if (!session) return <Login />

  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'akuntan'

  const menus = [
    { id: 'home', label: 'Beranda', icon: '🏠' },
    { id: 'anggota', label: 'Anggota', icon: '👥' },
    { id: 'transaksi', label: 'Transaksi', icon: '💸' },
    { id: 'pinjaman', label: 'Pinjaman', icon: '🏦' },
    { id: 'laporan', label: 'Laporan', icon: '📊' },
  ]

  const pages = {
    home: <Beranda koperasi={koperasi} />,
    anggota: <Anggota koperasi={koperasi} />,
    transaksi: <Transaksi koperasi={koperasi} userProfile={userProfile} isAdmin={isAdmin} />,
    pinjaman: <Pinjaman koperasi={koperasi} userProfile={userProfile} isAdmin={isAdmin} />,
    laporan: <Laporan koperasi={koperasi} />,
  }

  return (
    <div style={s.layout}>
      {/* SIDEBAR */}
      <div style={s.sidebar}>
        <div style={s.sidebarTop}>
          <div style={{ ...s.kopBadge, background: koperasi?.color || '#2563eb' }}>
            {koperasi?.nama?.charAt(0)?.toUpperCase() || 'K'}
          </div>
          <div style={s.kopNama}>{koperasi?.nama || 'KoperasiKu'}</div>
          <div style={s.roleBadge}>{userProfile?.role || 'user'}</div>
        </div>
        <nav style={s.nav}>
          {menus.map(m => (
            <button key={m.id} style={{ ...s.navBtn, ...(tab === m.id ? s.navActive : {}) }} onClick={() => setTab(m.id)}>
              <span>{m.icon}</span><span>{m.label}</span>
            </button>
          ))}
        </nav>
        <button style={s.logoutBtn} onClick={() => supabase.auth.signOut()}>🚪 Logout</button>
      </div>

      {/* CONTENT */}
      <div style={s.content}>{pages[tab] || null}</div>
    </div>
  )
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function login() {
    setLoading(true); setError('')
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setError(error.message)
    setLoading(false)
  }

  return (
    <div style={s.center}>
      <div style={s.loginCard}>
        <div style={s.loginIcon}>🏛</div>
        <h1 style={s.loginTitle}>KoperasiKu</h1>
        <p style={s.loginSub}>Sistem Manajemen Keuangan Koperasi</p>
        {error && <div style={s.errorBox}>{error}</div>}
        <input style={s.loginInput} placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && login()} />
        <input style={s.loginInput} placeholder="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && login()} />
        <button style={s.loginBtn} onClick={login} disabled={loading}>{loading ? 'Masuk...' : 'Masuk'}</button>
      </div>
    </div>
  )
}

// ─── BERANDA ──────────────────────────────────────────────────────────────────
function Beranda({ koperasi }) {
  const [stats, setStats] = useState({ simpanan: 0, pinjaman: 0, anggota: 0, pending: 0 })

  useEffect(() => { if (koperasi) fetchStats() }, [koperasi])

  async function fetchStats() {
    const [t, p, a] = await Promise.all([
      supabase.from('transaksi').select('jumlah, tipe, jenis, status').eq('koperasi_id', koperasi.id),
      supabase.from('pinjaman').select('jumlah, status').eq('koperasi_id', koperasi.id),
      supabase.from('anggota').select('id').eq('koperasi_id', koperasi.id),
    ])
    const trx = t.data || []
    const pinj = p.data || []
    const totalSimpanan = trx.filter(x => x.jenis === 'simpanan' && x.tipe === 'masuk' && x.status === 'approved').reduce((s, x) => s + x.jumlah, 0)
      - trx.filter(x => x.jenis === 'simpanan' && x.tipe === 'keluar' && x.status === 'approved').reduce((s, x) => s + x.jumlah, 0)
    const totalPinjaman = pinj.filter(x => x.status === 'aktif').reduce((s, x) => s + x.jumlah, 0)
    const pending = trx.filter(x => x.status === 'pending').length
    setStats({ simpanan: totalSimpanan, pinjaman: totalPinjaman, anggota: a.data?.length || 0, pending })
  }

  if (!koperasi) return (
    <div style={s.page}>
      <div style={s.emptyState}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
        <h3>Akun belum terhubung ke koperasi</h3>
        <p style={{ color: '#888' }}>Hubungi admin untuk setup akun Anda</p>
      </div>
    </div>
  )

  const cards = [
    { label: 'Total Anggota', value: stats.anggota + ' orang', color: '#2563eb', icon: '👥' },
    { label: 'Saldo Simpanan', value: fmt(stats.simpanan), color: '#16a34a', icon: '💰' },
    { label: 'Pinjaman Aktif', value: fmt(stats.pinjaman), color: '#9333ea', icon: '🏦' },
    { label: 'Transaksi Pending', value: stats.pending + ' transaksi', color: '#d97706', icon: '⏳' },
  ]

  return (
    <div style={s.page}>
      <div style={s.pageHeader}>
        <h2 style={s.pageTitle}>Beranda</h2>
        <p style={s.pageSub}>Selamat datang di {koperasi.nama}</p>
      </div>

      <div style={s.statsGrid}>
        {cards.map((c, i) => (
          <div key={i} style={{ ...s.statCard, borderTop: '3px solid ' + c.color }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>{c.icon}</div>
            <div style={{ ...s.statVal, color: c.color }}>{c.value}</div>
            <div style={s.statLabel}>{c.label}</div>
          </div>
        ))}
      </div>

      <div style={s.box}>
        <h3 style={s.boxTitle}>⚙️ Konfigurasi Aktif</h3>
        <div style={s.configGrid}>
          {[
            ['Bunga Pinjaman', `${koperasi.bunga_pinjaman}% / ${koperasi.periode_bunga} (${koperasi.metode_bunga})`],
            ['Simpanan Pokok', fmt(koperasi.simpanan_pokok)],
            ['Simpanan Wajib', fmt(koperasi.simpanan_wajib) + '/bln'],
            ['Denda Keterlambatan', `${koperasi.denda_keterlambatan}%/hari`],
            ['Masa Tenggang', `${koperasi.masa_tenggang} hari`],
            ['Simpanan Sukarela', koperasi.simpanan_sukarela ? 'Aktif' : 'Nonaktif'],
          ].map(([k, v], i) => (
            <div key={i} style={s.configItem}><span>{k}</span><b>{v}</b></div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── ANGGOTA ──────────────────────────────────────────────────────────────────
function Anggota({ koperasi }) {
  const [list, setList] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ nama: '', nik: '', no_hp: '', alamat: '', pekerjaan: '', tanggal_masuk: new Date().toISOString().split('T')[0] })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => { if (koperasi) fetchList() }, [koperasi])

  async function fetchList() {
    const { data } = await supabase.from('anggota').select('*').eq('koperasi_id', koperasi.id).order('created_at')
    if (data) setList(data)
  }

  async function tambah() {
    if (!form.nama.trim()) return alert('Nama wajib diisi')
    setSaving(true)
    const nomor = 'ANG-' + String(list.length + 1).padStart(4, '0')
    await supabase.from('anggota').insert({ ...form, koperasi_id: koperasi.id, nomor_anggota: nomor })
    await fetchList()
    setShowForm(false)
    setForm({ nama: '', nik: '', no_hp: '', alamat: '', pekerjaan: '', tanggal_masuk: new Date().toISOString().split('T')[0] })
    setSaving(false)
  }

  async function hapus(id) {
    if (!window.confirm('Hapus anggota ini?')) return
    await supabase.from('anggota').delete().eq('id', id)
    fetchList()
  }

  if (!koperasi) return <div style={s.page}><p style={{ color: '#888' }}>Koperasi belum tersedia</p></div>

  return (
    <div style={s.page}>
      <div style={s.pageHeader}>
        <div>
          <h2 style={s.pageTitle}>Anggota</h2>
          <p style={s.pageSub}>{list.length} anggota terdaftar</p>
        </div>
        <button style={s.btnBlue} onClick={() => setShowForm(!showForm)}>+ Tambah Anggota</button>
      </div>

      {showForm && (
        <div style={s.formCard}>
          <h3 style={s.formTitle}>Daftarkan Anggota Baru</h3>
          <p style={s.noteBlue}>Simpanan pokok ({fmt(koperasi.simpanan_pokok)}) otomatis dicatat saat pendaftaran.</p>
          <div style={s.formGrid}>
            {[
              ['Nama Lengkap *', 'nama', 'text', 'Nama lengkap'],
              ['NIK', 'nik', 'text', '16 digit NIK'],
              ['No. HP', 'no_hp', 'text', '08xx'],
              ['Pekerjaan', 'pekerjaan', 'text', 'Pekerjaan'],
            ].map(([label, key, type, ph]) => (
              <div key={key}>
                <label style={s.label}>{label}</label>
                <input style={s.input} type={type} value={form[key]} onChange={e => set(key, e.target.value)} placeholder={ph} />
              </div>
            ))}
            <div>
              <label style={s.label}>Tanggal Masuk</label>
              <input style={s.input} type="date" value={form.tanggal_masuk} onChange={e => set('tanggal_masuk', e.target.value)} />
            </div>
            <div>
              <label style={s.label}>Alamat</label>
              <input style={s.input} value={form.alamat} onChange={e => set('alamat', e.target.value)} placeholder="Alamat lengkap" />
            </div>
          </div>
          <div style={s.formActions}>
            <button style={s.btnGreen} onClick={tambah} disabled={saving}>{saving ? 'Menyimpan...' : '✓ Daftarkan'}</button>
            <button style={s.btnGray} onClick={() => setShowForm(false)}>Batal</button>
          </div>
        </div>
      )}

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead><tr>
            {['No. Anggota', 'Nama', 'NIK', 'No. HP', 'Pekerjaan', 'Tgl Masuk', 'Aksi'].map(h => <th key={h} style={s.th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {list.map(a => (
              <tr key={a.id}>
                <td style={s.td}><span style={s.noBadge}>{a.nomor_anggota}</span></td>
                <td style={s.td}><b>{a.nama}</b></td>
                <td style={s.td}>{a.nik || '-'}</td>
                <td style={s.td}>{a.no_hp || '-'}</td>
                <td style={s.td}>{a.pekerjaan || '-'}</td>
                <td style={s.td}>{fmtDate(a.tanggal_masuk)}</td>
                <td style={s.td}><button style={s.btnDanger} onClick={() => hapus(a.id)}>🗑</button></td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={7} style={s.tdEmpty}>Belum ada anggota</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── TRANSAKSI ────────────────────────────────────────────────────────────────
function Transaksi({ koperasi, userProfile, isAdmin }) {
  const [list, setList] = useState([])
  const [anggota, setAnggota] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ anggota_id: '', jenis: 'simpanan', tipe: 'masuk', keterangan: 'Simpanan Wajib', jumlah: '', tanggal: new Date().toISOString().split('T')[0] })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => { if (koperasi) { fetchList(); fetchAnggota() } }, [koperasi])

  async function fetchList() {
    const { data } = await supabase.from('transaksi').select('*, anggota(nama, nomor_anggota)').eq('koperasi_id', koperasi.id).order('created_at', { ascending: false })
    if (data) setList(data)
  }

  async function fetchAnggota() {
    const { data } = await supabase.from('anggota').select('*').eq('koperasi_id', koperasi.id)
    if (data) setAnggota(data)
  }

  async function catat() {
    if (!form.anggota_id) return alert('Pilih anggota dulu')
    if (!form.jumlah) return alert('Isi jumlah dulu')
    setSaving(true)
    const status = isAdmin ? 'approved' : 'pending'
    await supabase.from('transaksi').insert({
      ...form, jumlah: parseInt(form.jumlah), koperasi_id: koperasi.id,
      input_oleh: userProfile?.id, status,
      approved_oleh: isAdmin ? userProfile?.id : null,
      approved_at: isAdmin ? new Date().toISOString() : null,
    })
    await fetchList()
    setShowForm(false)
    setSaving(false)
  }

  async function approve(id) {
    await supabase.from('transaksi').update({ status: 'approved', approved_oleh: userProfile?.id, approved_at: new Date().toISOString() }).eq('id', id)
    fetchList()
  }

  async function reject(id) {
    await supabase.from('transaksi').update({ status: 'rejected' }).eq('id', id)
    fetchList()
  }

  const pending = list.filter(t => t.status === 'pending')
  const riwayat = list.filter(t => t.status !== 'pending')

  if (!koperasi) return <div style={s.page}><p style={{ color: '#888' }}>Koperasi belum tersedia</p></div>

  return (
    <div style={s.page}>
      <div style={s.pageHeader}>
        <div>
          <h2 style={s.pageTitle}>Transaksi</h2>
          <p style={s.pageSub}>{pending.length} transaksi menunggu persetujuan</p>
        </div>
        <button style={s.btnBlue} onClick={() => setShowForm(!showForm)}>+ Catat Transaksi</button>
      </div>

      {showForm && (
        <div style={s.formCard}>
          <h3 style={s.formTitle}>Catat Transaksi Baru</h3>
          {!isAdmin && <p style={s.noteYellow}>⏳ Transaksi masuk status PENDING sampai disetujui admin/akuntan.</p>}
          <div style={s.formGrid}>
            <div>
              <label style={s.label}>Anggota *</label>
              <select style={s.input} value={form.anggota_id} onChange={e => set('anggota_id', e.target.value)}>
                <option value="">-- Pilih Anggota --</option>
                {anggota.map(a => <option key={a.id} value={a.id}>{a.nomor_anggota} - {a.nama}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Jenis</label>
              <select style={s.input} value={form.jenis} onChange={e => set('jenis', e.target.value)}>
                <option value="simpanan">Simpanan</option>
                <option value="angsuran">Angsuran</option>
                <option value="pinjaman">Pinjaman</option>
              </select>
            </div>
            <div>
              <label style={s.label}>Tipe</label>
              <select style={s.input} value={form.tipe} onChange={e => set('tipe', e.target.value)}>
                <option value="masuk">Masuk</option>
                <option value="keluar">Keluar</option>
              </select>
            </div>
            <div>
              <label style={s.label}>Keterangan</label>
              <input style={s.input} value={form.keterangan} onChange={e => set('keterangan', e.target.value)} placeholder="Keterangan transaksi" />
            </div>
            <div>
              <label style={s.label}>Jumlah (Rp)</label>
              <input style={s.input} type="number" value={form.jumlah} onChange={e => set('jumlah', e.target.value)} placeholder="0" />
            </div>
            <div>
              <label style={s.label}>Tanggal</label>
              <input style={s.input} type="date" value={form.tanggal} onChange={e => set('tanggal', e.target.value)} />
            </div>
          </div>
          <div style={s.formActions}>
            <button style={s.btnGreen} onClick={catat} disabled={saving}>{saving ? 'Menyimpan...' : '✓ Catat'}</button>
            <button style={s.btnGray} onClick={() => setShowForm(false)}>Batal</button>
          </div>
        </div>
      )}

      {isAdmin && pending.length > 0 && (
        <div style={{ ...s.box, border: '1.5px solid #fde68a', background: '#fffbeb', marginBottom: 20 }}>
          <h3 style={s.boxTitle}>⏳ Menunggu Persetujuan ({pending.length})</h3>
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead><tr>
                {['Anggota', 'Keterangan', 'Jumlah', 'Tanggal', 'Aksi'].map(h => <th key={h} style={s.th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {pending.map(t => (
                  <tr key={t.id}>
                    <td style={s.td}><b>{t.anggota?.nama || '-'}</b><br /><span style={{ fontSize: 11, color: '#888' }}>{t.anggota?.nomor_anggota}</span></td>
                    <td style={s.td}>{t.keterangan}</td>
                    <td style={s.td}><b>{fmt(t.jumlah)}</b></td>
                    <td style={s.td}>{fmtDate(t.tanggal)}</td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={s.btnApprove} onClick={() => approve(t.id)}>✓ Setuju</button>
                        <button style={s.btnReject} onClick={() => reject(t.id)}>✗ Tolak</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={s.box}>
        <h3 style={s.boxTitle}>Riwayat Transaksi</h3>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead><tr>
              {['Tanggal', 'Anggota', 'Keterangan', 'Jumlah', 'Status'].map(h => <th key={h} style={s.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {riwayat.map(t => (
                <tr key={t.id}>
                  <td style={s.td}>{fmtDate(t.tanggal)}</td>
                  <td style={s.td}>{t.anggota?.nama || '-'}</td>
                  <td style={s.td}>{t.keterangan}</td>
                  <td style={s.td}><b style={{ color: t.tipe === 'keluar' ? '#dc2626' : '#16a34a' }}>{fmt(t.jumlah)}</b></td>
                  <td style={s.td}>
                    <span style={{ ...s.badge, background: t.status === 'approved' ? '#dcfce7' : '#fee2e2', color: t.status === 'approved' ? '#16a34a' : '#dc2626' }}>
                      {t.status === 'approved' ? 'Disetujui' : 'Ditolak'}
                    </span>
                  </td>
                </tr>
              ))}
              {!riwayat.length && <tr><td colSpan={5} style={s.tdEmpty}>Belum ada riwayat transaksi</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ─── PINJAMAN ─────────────────────────────────────────────────────────────────
function Pinjaman({ koperasi, userProfile, isAdmin }) {
  const [list, setList] = useState([])
  const [anggota, setAnggota] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ anggota_id: '', jumlah: 1000000, jangka_waktu: 12, tanggal_cair: new Date().toISOString().split('T')[0], tujuan: '' })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => { if (koperasi) { fetchList(); fetchAnggota() } }, [koperasi])

  async function fetchList() {
    const { data } = await supabase.from('pinjaman').select('*, anggota(nama, nomor_anggota)').eq('koperasi_id', koperasi.id).order('created_at', { ascending: false })
    if (data) setList(data)
  }

  async function fetchAnggota() {
    const { data } = await supabase.from('anggota').select('*').eq('koperasi_id', koperasi.id)
    if (data) setAnggota(data)
  }

  function hitungAngsuran(pokok, bunga, jangka, metode, periode) {
    const b = periode === 'tahun' ? bunga / 12 / 100 : bunga / 100
    if (metode === 'flat') return Math.round((pokok / jangka) + (pokok * b))
    if (b === 0) return Math.round(pokok / jangka)
    return Math.round(pokok * b * Math.pow(1 + b, jangka) / (Math.pow(1 + b, jangka) - 1))
  }

  const angsuran = koperasi ? hitungAngsuran(form.jumlah, koperasi.bunga_pinjaman, form.jangka_waktu, koperasi.metode_bunga, koperasi.periode_bunga) : 0

  async function ajukan() {
    if (!form.anggota_id) return alert('Pilih anggota dulu')
    setSaving(true)
    await supabase.from('pinjaman').insert({
      ...form, jumlah: parseInt(form.jumlah), jangka_waktu: parseInt(form.jangka_waktu),
      koperasi_id: koperasi.id, bunga_pinjaman: koperasi.bunga_pinjaman,
      metode_bunga: koperasi.metode_bunga, periode_bunga: koperasi.periode_bunga,
      angsuran_per_bulan: angsuran, status: 'aktif', terbayar: 0, angsuran_ke: 0,
    })
    await fetchList()
    setShowForm(false)
    setSaving(false)
  }

  async function bayar(p) {
    if (!window.confirm(`Bayar angsuran ke-${p.angsuran_ke + 1} sebesar ${fmt(p.angsuran_per_bulan)}?`)) return
    const angsuranKe = p.angsuran_ke + 1
    const terbayar = p.terbayar + p.angsuran_per_bulan
    const status = angsuranKe >= p.jangka_waktu ? 'lunas' : 'aktif'
    await supabase.from('pinjaman').update({ angsuran_ke: angsuranKe, terbayar, status }).eq('id', p.id)
    await supabase.from('transaksi').insert({
      koperasi_id: koperasi.id, anggota_id: p.anggota_id, pinjaman_id: p.id,
      jenis: 'angsuran', tipe: 'masuk', keterangan: `Angsuran ke-${angsuranKe}`,
      jumlah: p.angsuran_per_bulan, status: 'approved',
      input_oleh: userProfile?.id, approved_oleh: userProfile?.id,
      approved_at: new Date().toISOString(), tanggal: new Date().toISOString().split('T')[0],
    })
    fetchList()
  }

  if (!koperasi) return <div style={s.page}><p style={{ color: '#888' }}>Koperasi belum tersedia</p></div>

  return (
    <div style={s.page}>
      <div style={s.pageHeader}>
        <div>
          <h2 style={s.pageTitle}>Pinjaman</h2>
          <p style={s.pageSub}>{list.filter(p => p.status === 'aktif').length} pinjaman aktif</p>
        </div>
        <button style={s.btnBlue} onClick={() => setShowForm(!showForm)}>+ Ajukan Pinjaman</button>
      </div>

      {showForm && (
        <div style={s.formCard}>
          <h3 style={s.formTitle}>Pengajuan Pinjaman Baru</h3>
          <div style={s.formGrid}>
            <div>
              <label style={s.label}>Anggota *</label>
              <select style={s.input} value={form.anggota_id} onChange={e => set('anggota_id', e.target.value)}>
                <option value="">-- Pilih Anggota --</option>
                {anggota.map(a => <option key={a.id} value={a.id}>{a.nomor_anggota} - {a.nama}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Jumlah Pinjaman (Rp)</label>
              <input style={s.input} type="number" value={form.jumlah} onChange={e => set('jumlah', e.target.value)} />
            </div>
            <div>
              <label style={s.label}>Jangka Waktu (bulan)</label>
              <input style={s.input} type="number" value={form.jangka_waktu} onChange={e => set('jangka_waktu', e.target.value)} min="1" />
            </div>
            <div>
              <label style={s.label}>Tanggal Cair</label>
              <input style={s.input} type="date" value={form.tanggal_cair} onChange={e => set('tanggal_cair', e.target.value)} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={s.label}>Tujuan Pinjaman</label>
              <input style={s.input} value={form.tujuan} onChange={e => set('tujuan', e.target.value)} placeholder="Modal usaha, renovasi, dll" />
            </div>
          </div>
          <div style={s.noteBlue}>
            Angsuran: <b>{fmt(angsuran)}/bulan</b> · Total bayar: <b>{fmt(angsuran * form.jangka_waktu)}</b> · Bunga: {koperasi.bunga_pinjaman}%/{koperasi.periode_bunga} ({koperasi.metode_bunga})
          </div>
          <div style={s.formActions}>
            <button style={s.btnGreen} onClick={ajukan} disabled={saving}>{saving ? 'Menyimpan...' : '✓ Cairkan'}</button>
            <button style={s.btnGray} onClick={() => setShowForm(false)}>Batal</button>
          </div>
        </div>
      )}

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead><tr>
            {['Anggota', 'Jumlah', 'Jangka', 'Angsuran/bln', 'Progress', 'Status', 'Aksi'].map(h => <th key={h} style={s.th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {list.map(p => {
              const pct = Math.round((p.angsuran_ke / p.jangka_waktu) * 100)
              return (
                <tr key={p.id}>
                  <td style={s.td}><b>{p.anggota?.nama || '-'}</b><br /><span style={{ fontSize: 11, color: '#888' }}>{p.tujuan}</span></td>
                  <td style={s.td}>{fmt(p.jumlah)}</td>
                  <td style={s.td}>{p.jangka_waktu} bln</td>
                  <td style={s.td}>{fmt(p.angsuran_per_bulan)}</td>
                  <td style={s.td}>
                    <div style={{ fontSize: 11, marginBottom: 3 }}>{p.angsuran_ke}/{p.jangka_waktu}</div>
                    <div style={{ background: '#e5e7eb', borderRadius: 4, height: 6, width: 80 }}>
                      <div style={{ background: '#16a34a', borderRadius: 4, height: 6, width: `${pct}%` }} />
                    </div>
                  </td>
                  <td style={s.td}>
                    <span style={{ ...s.badge, background: p.status === 'lunas' ? '#dcfce7' : '#fef3c7', color: p.status === 'lunas' ? '#16a34a' : '#d97706' }}>
                      {p.status === 'lunas' ? 'Lunas' : 'Aktif'}
                    </span>
                  </td>
                  <td style={s.td}>
                    {p.status === 'aktif' && (
                      <button style={s.btnSmall} onClick={() => bayar(p)}>Bayar Angsuran</button>
                    )}
                  </td>
                </tr>
              )
            })}
            {!list.length && <tr><td colSpan={7} style={s.tdEmpty}>Belum ada data pinjaman</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── LAPORAN ──────────────────────────────────────────────────────────────────
function Laporan({ koperasi }) {
  const [transaksi, setTransaksi] = useState([])
  const [pinjaman, setPinjaman] = useState([])
  const [anggota, setAnggota] = useState([])

  useEffect(() => { if (koperasi) fetchAll() }, [koperasi])

  async function fetchAll() {
    const [t, p, a] = await Promise.all([
      supabase.from('transaksi').select('*, anggota(nama)').eq('koperasi_id', koperasi.id).eq('status', 'approved').order('created_at', { ascending: false }),
      supabase.from('pinjaman').select('*, anggota(nama)').eq('koperasi_id', koperasi.id),
      supabase.from('anggota').select('*').eq('koperasi_id', koperasi.id),
    ])
    if (t.data) setTransaksi(t.data)
    if (p.data) setPinjaman(p.data)
    if (a.data) setAnggota(a.data)
  }

  const totalSimpananMasuk = transaksi.filter(t => t.jenis === 'simpanan' && t.tipe === 'masuk').reduce((s, t) => s + t.jumlah, 0)
  const totalSimpananKeluar = transaksi.filter(t => t.jenis === 'simpanan' && t.tipe === 'keluar').reduce((s, t) => s + t.jumlah, 0)
  const totalAngsuran = transaksi.filter(t => t.jenis === 'angsuran').reduce((s, t) => s + t.jumlah, 0)
  const totalPinjaman = pinjaman.reduce((s, p) => s + p.jumlah, 0)
  const saldoSimpanan = totalSimpananMasuk - totalSimpananKeluar
  const sisaPiutang = totalPinjaman - pinjaman.reduce((s, p) => s + p.terbayar, 0)

  if (!koperasi) return <div style={s.page}><p style={{ color: '#888' }}>Koperasi belum tersedia</p></div>

  return (
    <div style={s.page}>
      <div style={s.pageHeader}>
        <div>
          <h2 style={s.pageTitle}>Laporan Keuangan</h2>
          <p style={s.pageSub}>Rekap posisi keuangan {koperasi.nama}</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div style={s.box}>
          <h3 style={s.boxTitle}>📊 Neraca Simpanan</h3>
          <div style={s.laporanRow}><span>Total Simpanan Masuk</span><b style={{ color: '#16a34a' }}>{fmt(totalSimpananMasuk)}</b></div>
          <div style={s.laporanRow}><span>Total Penarikan</span><b style={{ color: '#dc2626' }}>{fmt(totalSimpananKeluar)}</b></div>
          <div style={{ ...s.laporanRow, borderTop: '2px solid #e5e7eb', paddingTop: 8, marginTop: 4 }}>
            <b>Saldo Simpanan</b><b style={{ color: '#2563eb', fontSize: 18 }}>{fmt(saldoSimpanan)}</b>
          </div>
        </div>

        <div style={s.box}>
          <h3 style={s.boxTitle}>💰 Neraca Pinjaman</h3>
          <div style={s.laporanRow}><span>Total Pinjaman Disalurkan</span><b style={{ color: '#9333ea' }}>{fmt(totalPinjaman)}</b></div>
          <div style={s.laporanRow}><span>Total Angsuran Diterima</span><b style={{ color: '#16a34a' }}>{fmt(totalAngsuran)}</b></div>
          <div style={{ ...s.laporanRow, borderTop: '2px solid #e5e7eb', paddingTop: 8, marginTop: 4 }}>
            <b>Sisa Piutang</b><b style={{ color: '#dc2626', fontSize: 18 }}>{fmt(sisaPiutang)}</b>
          </div>
        </div>

        <div style={s.box}>
          <h3 style={s.boxTitle}>👥 Statistik Anggota</h3>
          <div style={s.laporanRow}><span>Total Anggota</span><b>{anggota.length} orang</b></div>
          <div style={s.laporanRow}><span>Pinjaman Aktif</span><b>{pinjaman.filter(p => p.status === 'aktif').length} kasus</b></div>
          <div style={s.laporanRow}><span>Pinjaman Lunas</span><b>{pinjaman.filter(p => p.status === 'lunas').length} kasus</b></div>
          <div style={s.laporanRow}><span>Total Transaksi</span><b>{transaksi.length} transaksi</b></div>
        </div>
      </div>

      <div style={s.box}>
        <h3 style={s.boxTitle}>📋 Riwayat Semua Transaksi (Approved)</h3>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead><tr>
              {['Tanggal', 'Anggota', 'Keterangan', 'Jenis', 'Jumlah'].map(h => <th key={h} style={s.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {transaksi.map((t, i) => (
                <tr key={i}>
                  <td style={s.td}>{fmtDate(t.tanggal)}</td>
                  <td style={s.td}>{t.anggota?.nama || '-'}</td>
                  <td style={s.td}>{t.keterangan}</td>
                  <td style={s.td}><span style={{ ...s.badge, background: t.tipe === 'keluar' ? '#fee2e2' : '#dcfce7', color: t.tipe === 'keluar' ? '#dc2626' : '#16a34a' }}>{t.tipe === 'keluar' ? 'Keluar' : 'Masuk'}</span></td>
                  <td style={s.td}><b>{fmt(t.jumlah)}</b></td>
                </tr>
              ))}
              {!transaksi.length && <tr><td colSpan={5} style={s.tdEmpty}>Belum ada transaksi</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const s = {
  // Layout
  layout: { display: 'flex', minHeight: '100vh', fontFamily: "'Segoe UI', system-ui, sans-serif", background: '#f8fafc' },
  sidebar: { width: 220, background: '#fff', borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', padding: '20px 0', position: 'sticky', top: 0, height: '100vh', flexShrink: 0 },
  sidebarTop: { textAlign: 'center', padding: '0 16px 20px', borderBottom: '1px solid #f0f0f0', marginBottom: 12 },
  kopBadge: { width: 48, height: 48, borderRadius: 14, margin: '0 auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 22 },
  kopNama: { fontWeight: 800, fontSize: 14, lineHeight: 1.3, marginBottom: 4 },
  roleBadge: { display: 'inline-block', padding: '2px 10px', background: '#eff6ff', color: '#2563eb', borderRadius: 20, fontSize: 11, fontWeight: 700, textTransform: 'capitalize' },
  nav: { flex: 1, display: 'flex', flexDirection: 'column', padding: '0 10px', gap: 2 },
  navBtn: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: 'none', background: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#555', textAlign: 'left', width: '100%' },
  navActive: { background: '#eff6ff', color: '#2563eb', fontWeight: 700 },
  logoutBtn: { margin: '0 10px', padding: '10px 14px', border: 'none', background: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, color: '#888', textAlign: 'left' },
  content: { flex: 1, overflow: 'auto' },

  // Login
  center: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f0f4ff 0%, #fafafa 100%)' },
  loginCard: { background: '#fff', borderRadius: 20, padding: 36, width: 360, boxShadow: '0 8px 40px rgba(0,0,0,0.10)', textAlign: 'center' },
  loginIcon: { fontSize: 48, marginBottom: 8 },
  loginTitle: { margin: '0 0 4px', fontSize: 26, fontWeight: 800 },
  loginSub: { color: '#888', fontSize: 13, marginBottom: 24 },
  loginInput: { width: '100%', padding: '11px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, marginBottom: 12, boxSizing: 'border-box', textAlign: 'left' },
  loginBtn: { width: '100%', padding: '12px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer' },
  errorBox: { background: '#fee2e2', color: '#dc2626', padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13, textAlign: 'left' },

  // Pages
  page: { padding: 28, maxWidth: 960 },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  pageTitle: { margin: '0 0 4px', fontSize: 22, fontWeight: 800 },
  pageSub: { color: '#888', fontSize: 13, margin: 0 },
  emptyState: { textAlign: 'center', padding: 60, color: '#555' },

  // Stats
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 24 },
  statCard: { background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' },
  statVal: { fontSize: 18, fontWeight: 800, marginBottom: 4 },
  statLabel: { fontSize: 12, color: '#555', fontWeight: 600 },

  // Boxes
  box: { background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.05)', marginBottom: 16 },
  boxTitle: { margin: '0 0 14px', fontSize: 15, fontWeight: 700 },
  configGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  configItem: { display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8, fontSize: 13 },
  laporanRow: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, borderBottom: '1px solid #f5f5f5' },

  // Forms
  formCard: { background: '#fff', borderRadius: 14, padding: 20, marginBottom: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' },
  formTitle: { margin: '0 0 8px', fontSize: 16, fontWeight: 700 },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  formActions: { display: 'flex', gap: 8, marginTop: 16 },
  label: { fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' },
  input: { width: '100%', padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' },
  noteBlue: { color: '#1e40af', fontSize: 12, marginBottom: 12, background: '#eff6ff', padding: '8px 12px', borderRadius: 8 },
  noteYellow: { color: '#92400e', fontSize: 12, marginBottom: 12, background: '#fffbeb', padding: '8px 12px', borderRadius: 8 },

  // Tables
  tableWrap: { overflow: 'auto', borderRadius: 12, border: '1.5px solid #e5e7eb' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '10px 14px', background: '#f8fafc', fontWeight: 700, textAlign: 'left', fontSize: 12, color: '#555', whiteSpace: 'nowrap' },
  td: { padding: '10px 14px', borderTop: '1px solid #f0f0f0', verticalAlign: 'middle' },
  tdEmpty: { padding: '24px 14px', textAlign: 'center', color: '#aaa', borderTop: '1px solid #f0f0f0' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 },
  noBadge: { background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700 },

  // Buttons
  btnBlue: { padding: '10px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  btnGreen: { padding: '10px 18px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  btnGray: { padding: '10px 18px', background: '#f1f5f9', color: '#555', border: '1.5px solid #e5e7eb', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  btnDanger: { padding: '6px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8, cursor: 'pointer' },
  btnSmall: { padding: '6px 12px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnApprove: { padding: '5px 12px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnReject: { padding: '5px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' },

  // Misc
  spinner: { width: 32, height: 32, border: '3px solid #e5e7eb', borderTop: '3px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite' },
}

